import ActivityPlaceholderCard from './ActivityPlaceholderCard'
import { isActivityPlaceholder } from '../utils/activityPlaceholder'
import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { useAppSelector } from 'hooks/reduxHooks'
import { isMaldivesCharterRegionEnabled } from 'selectors/maldivesSelectors'
import { useCharterItemFlightPrice } from 'tripPlanner/pages/chat/ChatTripPlannerPage/hooks/useCharterCartFlightPrices'
import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Image from 'components/Common/Image'
import NestableClickable from 'components/Common/NestableClickable'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import { isHotelSavedData } from 'tripPlanner/utils/savedItemDataGuards'
import CharterBundleLabel from './CharterBundleLabel'
import BodyText from 'LuxKit/Typography/BodyText'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Caption from 'LuxKit/Typography/Caption'
import TextButton from 'LuxKit/Button/TextButton'
import Divider from 'LuxKit/Divider'
import TextLink from 'LuxKit/TextLink'
import PriceRowPriceWithCaption from 'LuxKit/PricePoints/PriceRowPriceWithCaption'
import PriceDetails from 'tripPlanner/components/ItemOfferPrice/PriceDetails'
import BookedPriceDetails from 'tripPlanner/components/ItemOfferPrice/BookedPriceDetails'
import ExperienceTilePrices from 'components/Experiences/ExperienceTiles/ExperienceTilePrices'
import noop from 'lib/function/noop'
import CurrencyContext, { CurrencyProvider } from 'contexts/currencyContext'
import ChatItemDropdown from './ChatItemDropdown'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import { SVG_ICON_SIZES } from 'LuxKit/SvgIcon'
import NumberRating from 'components/Common/NumberRating/NumberRating'
import { STOP_VISIT_ROLE } from 'tripPlanner/api/types'
import type { TripItineraryItem, TripItemWithoutDay, StopVisitRole } from 'tripPlanner/api/types'
import GooglePlacePriceLevel, { shouldShowPriceLevel } from 'tripPlanner/components/GooglePlacePriceLevel'
import GooglePlaceRatingDisplay from 'tripPlanner/components/GooglePlaceRating/GooglePlaceRatingDisplay'
import { usePlaceRating } from 'tripPlanner/hooks/Google/usePlaceRating'
import { formatShortDateRange } from 'tripPlanner/utils'
import { INTL_SHORT_TIME_FORMAT_AM_PM } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import useWarnMissingCurrency from 'tripPlanner/pages/chat/ChatTripPlannerPage/hooks/useWarnMissingCurrency'
import {
  displayPricingFromDTO,
  getAccommodationUnavailableLabel,
  includesTaxesAndFees,
  isTotalMode,
  pricingUnavailableReason,
} from 'tripPlanner/utils/accommodationPricingDisplay'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { useOrder } from 'tripPlanner/hooks/api/order'
import { getBookedExperiencePrice, getBookedOfferPrice } from 'tripPlanner/utils/bookedOfferPricing'

const DESKTOP_TILE_IMAGE_SIZE = rem(102)
const MOBILE_TILE_IMAGE_SIZE = rem(40)
const ONGOING_STAY_BED_ICON = <LineBedIcon size="S" />
const BODY_TEXT_BLOCK_MEDIUM_GAP = rem(8)
const STAY_CAPTION_INDENT = `calc(${SVG_ICON_SIZES.S} + ${BODY_TEXT_BLOCK_MEDIUM_GAP})`

const TileContainer = styled(NestableClickable)`
  min-height: ${rem(120)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.L};
  display: flex;
  position: relative;
  gap: var(--itinerary-item-card-padding, ${rem(16)});
  padding: var(--itinerary-item-card-padding, ${rem(16)});
  width: 100%;
  min-width: 0;
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  cursor: pointer;
  text-align: start;

  &:hover {
    border-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  }

  &.compact {
    min-height: 0;
    flex-direction: column;
    gap: ${rem(8)};
  }
`

const HeaderRow = styled.div`
  display: flex;
  gap: ${rem(8)};
  align-items: flex-start;
  width: 100%;
  min-width: 0;
`

const TileImage = styled.div`
  width: ${DESKTOP_TILE_IMAGE_SIZE};
  flex-shrink: 0;
  align-self: flex-start;
  height: ${DESKTOP_TILE_IMAGE_SIZE};
  overflow: hidden;
  position: relative;
  border-radius: ${props => props.theme.borderRadius.M};

  ${mediaQueryUp.tablet} {
    align-self: stretch;
    height: auto;
  }

  .compact & {
    width: ${MOBILE_TILE_IMAGE_SIZE};
    height: ${MOBILE_TILE_IMAGE_SIZE};
    align-self: flex-start;
    border-radius: ${props => props.theme.borderRadius.L};
  }
`

const StyledImage = styled(Image)`
  position: absolute;
  inset-block-start: 0;
  inset-inline-start: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const HeaderContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  min-width: 0;
`

const ContentArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  min-width: 0;
`

const EndAligned = styled.div`
  margin-inline-start: auto;
`

const CondensedTextColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${rem(2)};
  min-width: 0;
`

const StayCaptionIndent = styled.div`
  padding-inline-start: ${STAY_CAPTION_INDENT};
`

const CondensedContainer = styled(NestableClickable)`
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.M};
  padding: ${rem(12)} var(--itinerary-item-card-padding, ${rem(16)});
  width: 100%;
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  cursor: pointer;
  text-align: start;

  &:hover {
    border-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  }

  &.mobile-stacked {
    display: flex;
    flex-direction: column;
    padding-block: ${rem(12)} ${rem(8)};
    padding-inline: ${rem(12)};
  }
`

interface Props {
  item: TripItineraryItem | TripItemWithoutDay
  /** StopVisitRole from the day context. Only present for day items. */
  role?: StopVisitRole
  /** True when this is the first day of an accommodation stay. */
  isCheckIn?: boolean
  /** 1-indexed day number within the stay. */
  dayOfStay?: number
  /** Total number of days in the stay. */
  totalDays?: number
  /** Pre-computed date range string for hotel stays (e.g., "24-26 April"). */
  stayDateRange?: string
  onDelete?: (itemId: number) => void
  onClick?: (googlePlaceId: string, placeName: string, itemId: number) => void
  onViewOfferDetails?: (item: TripItineraryItem | TripItemWithoutDay) => void
  onAddToTrip?: (item: TripItineraryItem | TripItemWithoutDay) => void
  onAskAI?: (item: TripItineraryItem | TripItemWithoutDay) => void
}

function ItineraryItemCard(props: Props) {
  const intl = useIntl()
  const isMobile = useScreenSizeOnly('mobile')
  const titleLineClamp = isMobile ? 2 : undefined
  const currentCurrency = useContext(CurrencyContext)
  const charterOfferId = useMemo(() => {
    const saved = props.item.savedItemData
    return isHotelSavedData(saved) && saved.source === 'offer' && saved.charterFlightBundle ? saved.offerId : undefined
  }, [props.item.savedItemData])
  const isMaldivesCharterEnabled = useAppSelector(isMaldivesCharterRegionEnabled)
  const {
    item,
    role,
    isCheckIn,
    dayOfStay,
    totalDays,
    stayDateRange,
    onDelete = noop,
    onClick = noop,
    onViewOfferDetails,
    onAddToTrip,
    onAskAI,
  } = props

  const dayOfStayLabel = intl.formatMessage(
    {
      id: 'YosgkXj7pPr-u_rXMbxk',
      defaultMessage: 'Day {dayOfStay} of {totalDays}',
      description: 'Caption showing the current day number within a hotel stay duration on the itinerary item card',
    },
    { dayOfStay, totalDays },
  )

  const viewDetailsLabel = intl.formatMessage({
    id: 'pTz2_TivJ1kKG7FhpOcS',
    defaultMessage: 'View details',
    description: 'Button label to view hotel details on the itinerary item card',
  })

  const bookingDetailsLabel = intl.formatMessage({
    id: 'V3if7yCh9Y75j9TcYdbF',
    defaultMessage: 'Booking details',
    description: 'CTA button text to view booking details in trip planner',
  })

  const savedItemData = item.savedItemData
  const offerData = isHotelSavedData(savedItemData) ? savedItemData : undefined
  const charterFlightBundle = offerData?.source === 'offer' ? offerData.charterFlightBundle : undefined
  const experienceData = savedItemData?.source === 'offer_experience' ? savedItemData : undefined
  const itemImageIds = savedItemData && 'imageIds' in savedItemData ? savedItemData.imageIds : undefined
  const isAccommodation = item.type === 'ACCOMMODATION'
  const isOngoingStay = isAccommodation && role === STOP_VISIT_ROLE.STAY && isCheckIn === false
  const hasHotelDetails = Boolean(offerData?.imageIds?.length)
  const hasExperienceDetails = Boolean(experienceData)
  const isBooked = 'isBooked' in item && item.isBooked
  const confirmationCode = 'confirmationCode' in item && typeof item.confirmationCode === 'string' ? item.confirmationCode : undefined
  const orderUrl = confirmationCode ? `/account/my-escapes/order/${confirmationCode}` : '/account/my-escapes'

  const firstImageId = useMemo(() => {
    if (itemImageIds?.length) return itemImageIds[0]
    return item.itemPlaceDetail?.imageIds?.[0]
  }, [itemImageIds, item.itemPlaceDetail?.imageIds])

  const googlePlaceId = item.itemPlaceDetail?.placeId

  // Ratings aren't part of the itinerary payload, so the compact mobile card fetches
  // them per place. Only non-accommodation cards show them — hotels use their LE rating.
  const { data: placeRating } = usePlaceRating(
    googlePlaceId ?? '',
    isMobile && !isAccommodation && !!googlePlaceId,
  )

  const handleDelete = useCallback(() => {
    onDelete(item.id)
  }, [item.id, onDelete])

  const handleClick = useCallback(() => {
    if (isAccommodation && hasHotelDetails && onViewOfferDetails) {
      onViewOfferDetails(item)
      return
    }
    if (hasExperienceDetails && onViewOfferDetails) {
      onViewOfferDetails(item)
      return
    }
    if (googlePlaceId) {
      onClick(googlePlaceId, item.name, item.id)
    }
  }, [isAccommodation, hasHotelDetails, hasExperienceDetails, onViewOfferDetails, item, googlePlaceId, onClick])

  const handleViewDetails = useCallback(() => {
    onViewOfferDetails?.(item)
  }, [onViewOfferDetails, item])

  const handleAddToTrip = useCallback(() => {
    onAddToTrip?.(item)
  }, [onAddToTrip, item])

  const handleAskAI = useCallback(() => {
    onAskAI?.(item)
  }, [onAskAI, item])

  const addToTripHandler = isAccommodation && onAddToTrip ? handleAddToTrip : undefined
  const askAIHandler = onAskAI ? handleAskAI : undefined

  const dateRangeText = useMemo(() => {
    if (!offerData?.checkIn || !offerData?.checkOut) return null
    return formatShortDateRange({
      startDate: moment(offerData.checkIn),
      endDate: moment(offerData.checkOut),
      includeYear: false,
    })
  }, [offerData?.checkIn, offerData?.checkOut])

  const resolvedDateText = dateRangeText ?? stayDateRange ?? null

  const checkInTime = useMemo(() => {
    if (!('startTime' in item) || !item.startTime) return null
    const startTime = moment(item.startTime)
    if (startTime.hours() === 0 && startTime.minutes() === 0) {
      return null
    }
    return intlDateFormatter(startTime, INTL_SHORT_TIME_FORMAT_AM_PM, intl.locale)
  }, [item, intl.locale])

  const sourceCurrency = offerData?.currencyCode ?? experienceData?.currencyCode
  const hasData = Boolean(offerData) || Boolean(experienceData)
  const missingCurrencyContext = useMemo(() => ({
    itemId: item.id,
    hasOfferData: Boolean(offerData),
    hasExperienceData: Boolean(experienceData),
  }), [item.id, offerData, experienceData])
  useWarnMissingCurrency(
    Boolean(hasData && !sourceCurrency),
    '[TripChat] Itinerary item missing currency',
    missingCurrencyContext,
  )

  const { flightPrice: charterFlightPrice, isCharterBundle: isCharterBundlePriced } = useCharterItemFlightPrice({
    tripItemId: item.id,
    offerId: charterOfferId,
    charterFlightBundle,
    checkIn: offerData?.checkIn,
    checkOut: offerData?.checkOut,
    occupancies: offerData?.occupancies,
  })

  const accommodationDisplayPricing = offerData?.pricing ? displayPricingFromDTO(offerData.pricing) : undefined
  const basePrice = accommodationDisplayPricing?.price ?? experienceData?.price

  const price = isCharterBundlePriced
    ? (basePrice !== undefined && charterFlightPrice !== undefined ? basePrice + charterFlightPrice : undefined)
    : basePrice
  const priceCurrency = offerData?.currencyCode ?? experienceData?.currencyCode
  const experienceMemberPrice = experienceData?.memberPrice
  const unavailableReason = pricingUnavailableReason(offerData?.pricing)
  const showAccommodationFooter = !!price || isBooked || !!unavailableReason || (onViewOfferDetails && hasHotelDetails)

  // Once booked, the saved pricing above is the offer's sell price, not what was charged —
  // only the order knows that, so booked cards read their price from it instead.
  const { data: bookedOrder, isLoading: isBookedOrderLoading } = useOrder(
    isBooked ? confirmationCode : undefined,
  )
  const bookedPrice = useMemo(() => {
    if (!isBooked || !bookedOrder) return undefined
    if (isAccommodation) {
      // spp items carry their offer identity as bedbankOfferId rather than offerId.
      const bookedOfferId = offerData?.source === 'offer' ? offerData.offerId : offerData?.bedbankOfferId
      return bookedOfferId ? getBookedOfferPrice(bookedOrder, bookedOfferId) : undefined
    }
    return experienceData ? getBookedExperiencePrice(bookedOrder, experienceData.experienceId) : undefined
  }, [isBooked, bookedOrder, isAccommodation, offerData, experienceData])
  const showBookedPrice = isBooked && (isBookedOrderLoading || !!bookedPrice)

  const luxPlusPrice = isCharterBundlePriced ? undefined : accommodationDisplayPricing?.luxPlusPrice
  // Only badge as a bundle when the charter region is enabled — otherwise checkout
  // wouldn't include (or charge for) the flight, so the label would be misleading.
  const showCharterBundleLabel = !!charterFlightBundle && isMaldivesCharterEnabled

  const fromLabel = intl.formatMessage({
    id: 'lrN1MpB7FBREq1ybic9U',
    defaultMessage: 'From',
    description: 'Price caption prefix meaning "starting from" on itinerary item card',
  })
  const totalPriceLabel = intl.formatMessage({
    id: 'xuYWAl0As4iEgyL591NZ',
    defaultMessage: 'Total',
    description: 'Price caption for a selected-rate accommodation total on itinerary item card',
  })
  const unavailableLabel = unavailableReason
    ? getAccommodationUnavailableLabel(unavailableReason, intl)
    : undefined

  if (isOngoingStay) {
    const stayCaption = dayOfStay !== undefined && totalDays !== undefined
      ? dayOfStayLabel
      : stayDateRange

    const showItemDropdown = isMobile || !isBooked
    const itemDropdown = showItemDropdown ? (
      <ChatItemDropdown onDelete={handleDelete} onAddToTrip={addToTripHandler} onAskAI={askAIHandler} />
    ) : undefined

    let detailsButton: React.ReactNode
    if (isBooked) {
      // No price here: this is a continuation day of a stay already priced on its check-in
      // card, so repeating the total on every day would read as a per-day charge.
      detailsButton = (
        <TextLink size="small" href={orderUrl} target="_blank" rel="noopener noreferrer">
          {bookingDetailsLabel}
        </TextLink>
      )
    } else if (onViewOfferDetails && hasHotelDetails) {
      detailsButton = (
        <TextButton size="small" kind="tertiary" onClick={handleViewDetails}>
          {viewDetailsLabel}
        </TextButton>
      )
    }

    if (isMobile) {
      return (
        <CondensedContainer className="mobile-stacked" onClick={handleClick}>
          <Group direction="horizontal" gap={8} verticalAlign="start" horizontalAlign="space-between" fullWidth>
            <CondensedTextColumn>
              <BodyTextBlock variant="medium" weight="bold" startIcon={ONGOING_STAY_BED_ICON} lineClamp={1}>
                {item.name}
              </BodyTextBlock>
              {stayCaption && (
                <StayCaptionIndent>
                  <Caption variant="large" colour="light-foreground-alt">
                    {stayCaption}
                  </Caption>
                </StayCaptionIndent>
              )}
            </CondensedTextColumn>
            {itemDropdown}
          </Group>
          {detailsButton && <EndAligned>{detailsButton}</EndAligned>}
        </CondensedContainer>
      )
    }

    let endActions: React.ReactNode
    if (isBooked) {
      endActions = detailsButton
    } else {
      endActions = (
        <Group direction="horizontal" gap={8} verticalAlign="center">
          {detailsButton}
          {itemDropdown}
        </Group>
      )
    }

    return (
      <CondensedContainer onClick={handleClick}>
        <Group direction="horizontal" gap={12} verticalAlign="center" horizontalAlign="space-between" fullWidth>
          <Group direction="horizontal" gap={12} verticalAlign="center">
            {ONGOING_STAY_BED_ICON}
            <BodyText variant="medium" weight="bold">{item.name}</BodyText>
            {stayCaption && (
              <Caption variant="large" colour="light-foreground-alt">
                {stayCaption}
              </Caption>
            )}
          </Group>
          {endActions}
        </Group>
      </CondensedContainer>
    )
  }

  if (isAccommodation) {
    if (isMobile) {
      return (
        <TileContainer className="compact" onClick={handleClick}>
          <HeaderRow>
            {firstImageId && (
              <TileImage>
                <StyledImage id={firstImageId} fit="center" />
              </TileImage>
            )}
            <HeaderContent>
              <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="start" fullWidth>
                <Group direction="vertical" gap={2}>
                  {showCharterBundleLabel && <CharterBundleLabel />}
                  <BodyText variant="medium" weight="bold" lineClamp={titleLineClamp}>{item.name}</BodyText>
                </Group>
                <ChatItemDropdown onDelete={handleDelete} onAddToTrip={addToTripHandler} onAskAI={askAIHandler} />
              </Group>

              <Group direction="horizontal" gap={8} verticalAlign="center" wrap="wrap" noDisplayWhenEmpty>
                {offerData?.rating !== undefined && offerData.rating > 0 && (
                  <NumberRating
                    rating={offerData.rating}
                    variant="xs"
                    hideSubtitle
                    inlineLabel
                  />
                )}

                {resolvedDateText && (
                  <>
                    {!!offerData?.rating && <Divider kind="secondary" direction="vertical" />}
                    <Caption variant="large" colour="light-foreground-alt">{resolvedDateText}</Caption>
                  </>
                )}

                {checkInTime && (
                  <>
                    {(!!offerData?.rating || resolvedDateText) && <Divider kind="secondary" direction="vertical" />}
                    <Caption variant="large" colour="light-foreground-alt">{checkInTime}</Caption>
                  </>
                )}
              </Group>
            </HeaderContent>
          </HeaderRow>

          {showAccommodationFooter && (
            <Group direction="horizontal" gap={16} verticalAlign="end" wrap="wrap" fullWidth>
              {showBookedPrice && (
                <BookedPriceDetails price={bookedPrice} isLoading={isBookedOrderLoading} />
              )}
              {!isBooked && price && (
                <PriceDetails
                  price={price}
                  luxPlusPrice={luxPlusPrice}
                  currencyCode={offerData?.currencyCode ?? currentCurrency}
                  priceLabel={offerData?.pricing && isTotalMode(offerData.pricing.mode) ? totalPriceLabel : fromLabel}
                  totalLabel={offerData?.saleUnit ?? 'total'}
                  showTaxesAndFees={offerData?.pricing ? includesTaxesAndFees(offerData.pricing) : false}
                />
              )}
              {!isBooked && !price && unavailableLabel && (
                <BodyText variant="large" weight="bold">
                  {unavailableLabel}
                </BodyText>
              )}
              {onViewOfferDetails && hasHotelDetails && (
                <EndAligned>
                  {isBooked ? (
                    <TextLink size="medium" onClick={handleViewDetails}>
                      {bookingDetailsLabel}
                    </TextLink>
                  ) : (
                    <TextButton size="medium" kind="primary" onClick={handleViewDetails}>
                      {viewDetailsLabel}
                    </TextButton>
                  )}
                </EndAligned>
              )}
            </Group>
          )}
        </TileContainer>
      )
    }

    return (
      <TileContainer onClick={handleClick}>
        {firstImageId && (
          <TileImage>
            <StyledImage id={firstImageId} fit="center" />
          </TileImage>
        )}
        <ContentArea>
          <Group direction="vertical" gap={8} fullWidth>
            <Group direction="vertical" fullWidth>
              <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="start" fullWidth>
                <Group direction="vertical" gap={2}>
                  {showCharterBundleLabel && <CharterBundleLabel />}
                  <Heading variant="heading6" lineClamp={titleLineClamp}>{item.name}</Heading>
                </Group>
                <ChatItemDropdown onDelete={handleDelete} onAddToTrip={addToTripHandler} onAskAI={askAIHandler} />
              </Group>

              <Group direction="horizontal" gap={8} verticalAlign="center" wrap="wrap" noDisplayWhenEmpty>
                {offerData?.rating !== undefined && offerData.rating > 0 && (
                  <NumberRating
                    rating={offerData.rating}
                    variant="xs"
                    hideSubtitle
                    inlineLabel
                  />
                )}

                {resolvedDateText && (
                  <>
                    {!!offerData?.rating && <Divider kind="secondary" direction="vertical" />}
                    <Caption variant="large" colour="light-foreground-alt">{resolvedDateText}</Caption>
                  </>
                )}

                {checkInTime && (
                  <>
                    {(!!offerData?.rating || resolvedDateText) && <Divider kind="secondary" direction="vertical" />}
                    <Caption variant="large" colour="light-foreground-alt">{checkInTime}</Caption>
                  </>
                )}
              </Group>
            </Group>
            {item.description && (
              <BodyText variant="medium" colour="light-foreground-alt">
                {item.description}
              </BodyText>
            )}
          </Group>

          {showAccommodationFooter && (
            <Group direction="horizontal" gap={16} verticalAlign="end" wrap="wrap" fullWidth>
              {showBookedPrice && (
                <BookedPriceDetails price={bookedPrice} isLoading={isBookedOrderLoading} />
              )}
              {!isBooked && price && (
                <PriceDetails
                  price={price}
                  luxPlusPrice={luxPlusPrice}
                  currencyCode={offerData?.currencyCode ?? currentCurrency}
                  priceLabel={offerData?.pricing && isTotalMode(offerData.pricing.mode) ? totalPriceLabel : fromLabel}
                  totalLabel={offerData?.saleUnit ?? 'total'}
                  showTaxesAndFees={offerData?.pricing ? includesTaxesAndFees(offerData.pricing) : false}
                />
              )}
              {!isBooked && !price && unavailableLabel && (
                <BodyText variant="large" weight="bold">
                  {unavailableLabel}
                </BodyText>
              )}
              {onViewOfferDetails && hasHotelDetails && (
                <EndAligned>
                  {isBooked ? (
                    <TextLink size="medium" onClick={handleViewDetails}>
                      {bookingDetailsLabel}
                    </TextLink>
                  ) : (
                    <TextButton size="medium" kind="primary" onClick={handleViewDetails}>
                      {viewDetailsLabel}
                    </TextButton>
                  )}
                </EndAligned>
              )}
            </Group>
          )}
        </ContentArea>
      </TileContainer>
    )
  }

  if (isActivityPlaceholder(item)) {
    return <ActivityPlaceholderCard item={item} onDelete={handleDelete} onAskAI={askAIHandler} />
  }

  const placeDetail = item.itemPlaceDetail
  const priceLevel = placeDetail?.priceLevel
  const showPriceLevel = shouldShowPriceLevel(priceLevel)
  const hasOpeningHours = Boolean(placeDetail?.openingHours?.length)

  const categoryLabel = placeDetail?.primaryTypeDisplayName
  const ratingValue = placeRating?.rating
  const showRating = ratingValue !== undefined && ratingValue > 0
  const showPlaceMetaRow = showRating || !!categoryLabel || showPriceLevel

  const bookViaVenueLabel = intl.formatMessage({
    id: '1ZES2H8MvVxJQp4nvtjW',
    defaultMessage: 'Booking may be required — check venue details',
    description: 'Label shown on itinerary item tile when the venue has opening hours and can be booked directly',
  })

  if (isMobile) {
    return (
      <TileContainer className="compact" onClick={handleClick}>
        <HeaderRow>
          {firstImageId && (
            <TileImage>
              <StyledImage id={firstImageId} fit="center" />
            </TileImage>
          )}
          <HeaderContent>
            <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="start" fullWidth>
              <Group direction="vertical" gap={2}>
                {showCharterBundleLabel && <CharterBundleLabel />}
                <BodyText variant="medium" weight="bold" lineClamp={titleLineClamp}>{item.name}</BodyText>
              </Group>
              <ChatItemDropdown onDelete={handleDelete} onAskAI={askAIHandler} />
            </Group>

            {showPlaceMetaRow && (
              <Group direction="horizontal" gap={8} verticalAlign="center" wrap="wrap">
                {showRating && (
                  <GooglePlaceRatingDisplay
                    rating={ratingValue}
                    ratingsCount={placeRating?.userRatingCount}
                    size="S"
                  />
                )}

                {categoryLabel && (
                  <>
                    {showRating && <Divider kind="secondary" direction="vertical" />}
                    <Caption variant="medium" colour="light-foreground-alt" lineClamp={1}>{categoryLabel}</Caption>
                  </>
                )}

                {showPriceLevel && (
                  <>
                    {(showRating || !!categoryLabel) && <Divider kind="secondary" direction="vertical" />}
                    <GooglePlacePriceLevel priceLevel={priceLevel} size="small" colour="light-foreground-alt" />
                  </>
                )}
              </Group>
            )}
          </HeaderContent>
        </HeaderRow>
        {hasOpeningHours && (
          <BodyTextBlock variant="small" startIcon={<LineCalendarIcon />} weight="bold">
            {bookViaVenueLabel}
          </BodyTextBlock>
        )}
        {(isBooked || price || (onViewOfferDetails && hasExperienceDetails)) && (
          <Group direction="horizontal" gap={16} verticalAlign="end" wrap="wrap" fullWidth>
            {showBookedPrice && (
              <BookedPriceDetails price={bookedPrice} isLoading={isBookedOrderLoading} />
            )}
            {!isBooked && price && experienceData && (
              <CurrencyProvider currency={priceCurrency}>
                <Group direction="vertical" gap={4}>
                  <ExperienceTilePrices
                    totalPrice={price}
                    totalMemberPrice={experienceMemberPrice}
                    totalGrossPrice={experienceData?.value}
                    discountPercentage={experienceData?.discountPercent}
                    discountMemberPercentage={experienceData?.memberDiscountPercent}
                    isFromPrice
                    ticketUnitLabel="person"
                    size="S"
                  />
                </Group>
              </CurrencyProvider>
            )}
            {!isBooked && price && !experienceData && (
              <CurrencyProvider currency={offerData?.currencyCode}>
                <PriceRowPriceWithCaption
                  size="S"
                  price={price}
                  caption={intl.formatMessage({
                    id: 'lrN1MpB7FBREq1ybic9U',
                    defaultMessage: 'From',
                    description: 'Price caption prefix meaning "starting from" on itinerary item card',
                  })}
                  saleUnit={offerData?.saleUnit ?? 'total'}
                />
              </CurrencyProvider>
            )}
            {isBooked && (
              <EndAligned>
                <TextLink size="medium" href={orderUrl} target="_blank" rel="noopener noreferrer">
                  {bookingDetailsLabel}
                </TextLink>
              </EndAligned>
            )}
            {!isBooked && onViewOfferDetails && hasExperienceDetails && (
              <EndAligned>
                <TextButton size="medium" kind="primary" onClick={handleViewDetails}>
                  {viewDetailsLabel}
                </TextButton>
              </EndAligned>
            )}
          </Group>
        )}
      </TileContainer>
    )
  }

  return (
    <TileContainer onClick={handleClick}>
      {firstImageId && (
        <TileImage>
          <StyledImage id={firstImageId} fit="center" />
        </TileImage>
      )}
      <ContentArea>
        <Group direction="vertical" gap={4} fullWidth>
          <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="start" fullWidth>
            <Group direction="vertical" gap={2}>
              {showCharterBundleLabel && <CharterBundleLabel />}
              <Group direction="horizontal" gap={8} verticalAlign="baseline" wrap="wrap" noDisplayWhenEmpty>
                <Heading variant="heading6" lineClamp={titleLineClamp}>{item.name}</Heading>
                {showPriceLevel && (
                  <GooglePlacePriceLevel priceLevel={priceLevel} size="small" colour="light-foreground-alt" />
                )}
              </Group>
            </Group>
            <ChatItemDropdown onDelete={handleDelete} onAskAI={askAIHandler} />
          </Group>
          {item.description && (
            <BodyText variant="medium">
              {item.description}
            </BodyText>
          )}
          {hasOpeningHours && (
            <BodyTextBlock variant="small" startIcon={<LineCalendarIcon />} weight="bold">
              {bookViaVenueLabel}
            </BodyTextBlock>
          )}
        </Group>
        {(isBooked || price || (onViewOfferDetails && hasExperienceDetails)) && (
          <Group direction="horizontal" gap={16} verticalAlign="end" wrap="wrap" fullWidth>
            {showBookedPrice && (
              <BookedPriceDetails price={bookedPrice} isLoading={isBookedOrderLoading} />
            )}
            {!isBooked && price && experienceData && (
              <CurrencyProvider currency={priceCurrency}>
                <Group direction="vertical" gap={4}>
                  <ExperienceTilePrices
                    totalPrice={price}
                    totalMemberPrice={experienceMemberPrice}
                    totalGrossPrice={experienceData?.value}
                    discountPercentage={experienceData?.discountPercent}
                    discountMemberPercentage={experienceData?.memberDiscountPercent}
                    isFromPrice
                    ticketUnitLabel="person"
                    size="S"
                  />
                </Group>
              </CurrencyProvider>
            )}
            {!isBooked && price && !experienceData && (
              <CurrencyProvider currency={offerData?.currencyCode}>
                <PriceRowPriceWithCaption
                  size="S"
                  price={price}
                  caption={intl.formatMessage({
                    id: 'lrN1MpB7FBREq1ybic9U',
                    defaultMessage: 'From',
                    description: 'Price caption prefix meaning "starting from" on itinerary item card',
                  })}
                  saleUnit={offerData?.saleUnit ?? 'total'}
                />
              </CurrencyProvider>
            )}
            {isBooked && (
              <EndAligned>
                <TextLink size="medium" href={orderUrl} target="_blank" rel="noopener noreferrer">
                  {bookingDetailsLabel}
                </TextLink>
              </EndAligned>
            )}
            {!isBooked && onViewOfferDetails && hasExperienceDetails && (
              <EndAligned>
                <TextButton size="medium" kind="primary" onClick={handleViewDetails}>
                  {viewDetailsLabel}
                </TextButton>
              </EndAligned>
            )}
          </Group>
        )}
      </ContentArea>
    </TileContainer>
  )
}

export default ItineraryItemCard
